import React, { useState, useEffect, useCallback } from "react";
import { useFetchAllJobTasks } from "@labeling-tools/api/lebron/react-query-hooks";
import { getCurrentUser } from "@/utils/auth";
import { JobTaskStatus } from "@augmodo/lebron-browser-client";
import {
	Table,
	Group,
	Text,
	Pagination,
	TextInput,
	Button,
} from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState, setSearchUsername } from "@/store";
import { useGate } from "statsig-react";

export const QaFeedbackTabContent = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [fetchTasksParams, setFetchTasksParams] = useState<any>(null);
	const [searchInputValue, setSearchInputValue] = useState<string>("");
	const { data: userTasks, isLoading: tasksLoading } =
		useFetchAllJobTasks(fetchTasksParams);
	const { value: isAdmin } = useGate("out_of_stock_admin");
	const searchUsername = useAppSelector(
		(state: RootState) => state.oos.searchUsername,
	);

	const meOrAll = isAdmin ? "all" : "me";

	useEffect(() => {
		const asycnFetchTasks = async () => {
			const user = await getCurrentUser();
			if (user?.uid) {
				setFetchTasksParams({
					assignee: searchUsername?.length ? searchUsername : meOrAll,
					type: "OUT_OF_STOCK_CONFIRMATION_QA",
					status: JobTaskStatus.COMPLETED,
					ordering: "-created_at",
					qa_confirmation_correct: false,
				});
			}
		};
		asycnFetchTasks();
	}, [searchUsername]);

	const handleRowClick = useCallback(
		(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, task: any) => {
			const to = `/jobs/${task?.job_id}/confirmation-qa-feedback?taskId=${task?.id}`;
			if (e.metaKey || e.ctrlKey) {
				window.open(to, "_blank");
			} else {
				navigate(to);
			}
		},
		[navigate],
	);

	if (!userTasks) {
		return <div>Loading...</div>;
	}

	const noTasksFound =
		userTasks?.tasks?.length === 0 && !tasksLoading && !!fetchTasksParams;

	const rows = userTasks?.tasks?.map((task) => (
		<Table.Tr
			key={task.id}
			onClick={(e) => handleRowClick(e, task)}
			style={{ cursor: "pointer" }}
			className="hover:bg-gray-50 transition-colors"
		>
			<Table.Td>
				<Group gap="xs">
					<Text fw={600} size="sm">
						<Link
							to={`/jobs/${task?.job_id}/confirmation-qa-feedback?taskId=${task?.id}`}
							style={{ textDecoration: "underline" }}
							onClick={(e) => e.stopPropagation()}
						>
							{task?.metadata?.source_task_id as string}
						</Link>
					</Text>
				</Group>
			</Table.Td>
			<Table.Td>{task?.metadata?.source_job_id as string}</Table.Td>
			<Table.Td>{task?.metadata?.source_task_user as string}</Table.Td>
			<Table.Td>{new Date(task?.created_at).toLocaleDateString()}</Table.Td>
		</Table.Tr>
	));
	return (
		<div>
			{isAdmin && (
				<Group gap="sm" mb="md" className="max-w-md justify-end">
					<TextInput
						placeholder="Search by username or email"
						value={searchInputValue}
						onChange={(e) => setSearchInputValue(e.currentTarget.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								dispatch(setSearchUsername(e.currentTarget.value));
							}
						}}
						leftSection={<IconSearch size={16} />}
						style={{ flex: 1 }}
					/>
					<Button onClick={() => dispatch(setSearchUsername(searchInputValue))}>
						Search
					</Button>
				</Group>
			)}

			{noTasksFound ? (
				<div>No tasks found</div>
			) : (
				<>
					<Table withTableBorder stickyHeader>
						<Table.Thead>
							<Table.Tr>
								<Table.Th>Original Task ID</Table.Th>
								<Table.Th style={{ textAlign: "center" }}>
									Original Job ID
								</Table.Th>
								<Table.Th style={{ textAlign: "center" }}>
									Completed By
								</Table.Th>
								<Table.Th style={{ textAlign: "center" }}>
									Completion Date
								</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>{rows}</Table.Tbody>
					</Table>
					<div className="flex justify-center mt-6">
						<Pagination
							total={userTasks?.total_pages}
							value={userTasks?.page}
							onChange={(value) => {
								setFetchTasksParams({
									...fetchTasksParams,
									page: value,
								});
							}}
							size="sm"
						/>
					</div>
				</>
			)}
		</div>
	);
};
